import { useMutation } from '@apollo/client'
import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import useAuth from 'data/gradoo/hooks/useAuth'
import {
  CREATE_NEW_AUTO_PROJECT,
  CREATE_NEW_PROJECT
} from 'data/layoutcreator/mutations/projects'
import { GET_PROJECTS } from 'data/layoutcreator/queries/projects'
import Templates from 'shared/components/Templates'
import Categories from 'shared/components/TemplatesCategories'
import { TemplateEntity } from 'shared/types/layoutcreator/graphql'
import Button, { Intents } from 'shared/ui/Button'
import Checkbox from 'shared/ui/Checkbox'
import Flex from 'shared/ui/Flex'
import { FormError } from 'shared/ui/FormInput'
import StepsBar from 'shared/ui/StepsBar'
import Text from 'shared/ui/Text'

import { Steps } from '../../index'
import { LocalStorageKeys, NewProjectModalStepIds } from '../../types'
import FooterContainer from '../FooterContainer'
import StepContent from '../StepContent'
import StepFooter from '../StepFooter'
import StepForm from '../StepForm'
import { clearNewProjectLocalStorageData } from './utils'

const AUTOMATION_ENTITIES_OPTIONS = [
  {
    label: 'Profiles',
    key: 'profiles'
  },
  {
    label: 'Quotes',
    key: 'quotes'
  },
  {
    label: 'Collages',
    key: 'collages'
  },
  {
    label: 'Rankings',
    key: 'rankings'
  },
  {
    label: 'Reports',
    key: 'reports'
  }
]

const ALL_ENTITIES_KEYS = AUTOMATION_ENTITIES_OPTIONS.map(
  option => option.key
)

type TemplateProps = {
  setStep: Dispatch<SetStateAction<NewProjectModalStepIds>>
  isAutoProject: boolean
  setAutoProjectFlag: Dispatch<SetStateAction<boolean>>
  groupId?: string
  closeModal: () => void
}

const Template = ({
  setStep,
  isAutoProject,
  setAutoProjectFlag,
  groupId: passedGroupId,
  closeModal
}: TemplateProps) => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const [templateError, setTemplateError] = useState<string>('')
  const [currentCategoryId, setCurrentCategoryId] = useState<
    string | null
  >(null)
  const [mockData, setMockData] = useState<boolean>(() => {
    return localStorage.getItem(LocalStorageKeys.mockData) === 'true'
  })

  const { authGroupId } = useAuth()
  const groupId = passedGroupId || authGroupId!

  const [createProject, { loading, error }] = useMutation(
    CREATE_NEW_PROJECT,
    {
      refetchQueries: [
        { query: GET_PROJECTS, variables: { groupId } }
      ]
    }
  )

  const [
    createAutoProject,
    { loading: autoLoading, error: autoError }
  ] = useMutation(CREATE_NEW_AUTO_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS, variables: { groupId } }]
  })
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedTemplateId) {
      setTemplateError(formatMessage({ id: 'Template.select.error' }))
      return false
    }

    const isMockData =
      localStorage.getItem(LocalStorageKeys.mockData) === 'true'
    const isAdminOnly =
      localStorage.getItem(LocalStorageKeys.adminOnly) === 'true'

    const variables = {
      templatePath: selectedTemplateId,
      groupId,
      projectName: localStorage.getItem(
        LocalStorageKeys.title
      ) as string,
      coverType: localStorage.getItem(
        LocalStorageKeys.coverType
      ) as string,
      adminOnly: isAdminOnly,
      mockData: isMockData ? true : undefined,
      entities: isAutoProject
        ? isAllEntitiesSelected
          ? ['all']
          : selectedEntities
        : undefined
    }

    try {
      if (isAutoProject) {
        const result = await createAutoProject({ variables })
        if (result.data?.createAutoProject) {
          const { projectId } = result.data.createAutoProject
          if (projectId) {
            navigate(`/${projectId}/content`)
          } else {
            navigate('/')
          }
        }
      } else {
        const result = await createProject({ variables })
        if (result.data?.createNewProject) {
          clearNewProjectLocalStorageData()
          navigate('/')
        }
      }
    } catch {
      toast.error(
        formatMessage({ id: 'New.project.creation.failed' })
      )
    }

    closeModal()
  }

  useEffect(() => {
    if (error || autoError) {
      console.error(isAutoProject ? autoError : error)
    }
  }, [error, autoError, isAutoProject])

  const [selectedTemplateId, setSelectedTemplateId] =
    useState<string>()

  const handleMockDataChange = (value: boolean) => {
    localStorage.setItem(LocalStorageKeys.mockData, value.toString())
    setMockData(value)
  }

  // Automation entities
  const [isAllEntitiesSelected, setIsAllEntitiesSelected] =
    useState<boolean>(true)
  const [selectedEntities, setSelectedEntities] = useState<string[]>(
    AUTOMATION_ENTITIES_OPTIONS.map(option => option.key)
  )

  const handleEntitiesAllCheckboxChange = (value: boolean) => {
    setIsAllEntitiesSelected(value)

    if (value) {
      setSelectedEntities(ALL_ENTITIES_KEYS)
    }
  }

  const handleEntitiesCheckboxChange = (
    key: string,
    value: boolean
  ) => {
    if (isAllEntitiesSelected) {
      return
    }

    setSelectedEntities(
      value
        ? [...selectedEntities, key]
        : selectedEntities.filter((entity: string) => entity !== key)
    )
  }

  return (
    <StepForm onSubmit={handleSubmit}>
      <StepContent>
        <Flex width="100%" columnGap={64} alignItems="flex-start">
          <Flex direction="column">
            <Categories
              currentCategoryId={currentCategoryId}
              onCategorySelect={setCurrentCategoryId}
            />

            {isAutoProject && (
              <Flex direction="column" rowGap={12} marginTop={24}>
                <Text
                  tag="h3"
                  size={14}
                  weight={600}
                  text="Modules"
                />

                <Checkbox
                  key="all"
                  checked={isAllEntitiesSelected}
                  onChange={value =>
                    handleEntitiesAllCheckboxChange(value)
                  }
                  label="All"
                />

                <Flex direction="column" rowGap={4}>
                  {AUTOMATION_ENTITIES_OPTIONS.map(option => (
                    <Checkbox
                      key={option.key}
                      checked={selectedEntities.includes(
                        option.key as string
                      )}
                      onChange={value =>
                        handleEntitiesCheckboxChange(
                          option.key,
                          value
                        )
                      }
                      disabled={isAllEntitiesSelected}
                      label={option.label}
                    />
                  ))}
                </Flex>
              </Flex>
            )}
          </Flex>
          <Templates
            isAutoProject={isAutoProject}
            setSelectedTemplateId={setSelectedTemplateId}
            setTemplateError={setTemplateError}
            selectedTemplateId={selectedTemplateId}
            filter={(template: TemplateEntity) =>
              currentCategoryId
                ? template.categoryId === currentCategoryId
                : true
            }
          />
          {templateError && (
            <Flex justifyContent="center" marginTop={16}>
              <FormError>{templateError}</FormError>
            </Flex>
          )}
        </Flex>
      </StepContent>
      <FooterContainer>
        <Button
          labelId="New.project.go.back"
          type="button"
          onClick={() => {
            if (Steps.template.prevStepId) {
              setStep(Steps.template.prevStepId)
            }
          }}
          intent={Intents.neutral}
        />
        <StepsBar<NewProjectModalStepIds>
          stepId={NewProjectModalStepIds.template}
          steps={Steps}
          setStep={setStep}
        />
        <StepFooter
          mockData={mockData}
          onMockDataChange={handleMockDataChange}
          setAutoProjectFlag={setAutoProjectFlag}
          stepId={NewProjectModalStepIds.template}
          isLoading={loading || autoLoading}
          setStep={setStep}
        />
      </FooterContainer>
    </StepForm>
  )
}

export default Template
